// Единственное место, где приложение ходит на сервер. Ошибки приводятся к
// одному виду, 401 разлогинивает — иначе каждый вызывающий писал бы это сам.

import { state } from './state.js';

/** Ошибка с кодом ответа: по нему видно, есть ли смысл повторять. */
function fail(message, status) {
  return Object.assign(new Error(message), { status });
}

async function request(url, options = {}) {
  let res;
  try {
    res = await fetch(url, options);
  } catch (e) {
    // Сеть не ответила вовсе: сервер перезапускают, вайфай моргнул. Кода нет —
    // значит повторять можно и нужно.
    throw fail('сеть не отвечает: ' + e.message, 0);
  }

  if (res.status === 401) {
    state.user = null;
    throw fail('нужно войти', 401);
  }

  const type = res.headers.get('content-type') || '';
  if (!type.includes('application/json')) {
    if (!res.ok) throw fail(`ошибка ${res.status}`, res.status);
    return res;
  }

  const data = await res.json().catch(() => ({ error: `ошибка ${res.status}` }));
  // Судим по коду ответа, а не по наличию поля error в теле. Поле там бывает
  // законным содержимым: ход установки честно рассказывает, чем она кончилась,
  // и ответ при этом успешный. Пока запрос считался неудачным по одному лишь
  // наличию поля, упавшая установка показывала «связь с сайтом прервалась» —
  // и список серверов после неё не обновлялся, навсегда застревая в «ставится».
  if (!res.ok) throw fail(data.error || `ошибка ${res.status}`, res.status);
  return data;
}

const json = (method) => (url, body) => request(url, {
  method,
  headers: { 'content-type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

export const api = {
  get: (url) => request(url),
  post: json('POST'),
  put: json('PUT'),
  patch: json('PATCH'),
  del: json('DELETE'),

  /**
   * Сырое тело: файлы и аудио.
   *
   * Тип указываем обязательно. fetch проставляет его сам только для Blob (у
   * него тип свой), а для ArrayBuffer — не проставляет вовсе. Без заголовка
   * express.raw тело не разбирает, и сервер честно отвечает «пустой файл»:
   * из-за этого приложить материалы или файл вакансии из браузера было нельзя
   * вообще никак, хотя тот же файл через curl принимался.
   */
  raw: (url, body, headers = {}) => request(url, {
    method: 'POST',
    headers: body instanceof Blob ? headers : { 'content-type': 'application/octet-stream', ...headers },
    body,
  }),

  /**
   * Отправка, переживающая обрыв. Ключ делает повтор безопасным: сервер по нему
   * находит ту же работу, а не запускает новую — поэтому перезапуск сервера
   * посреди хода больше не стоит ни реплики, ни оплаченного ответа модели.
   *
   * Повторяем только то, что имеет смысл повторять: сеть молчит или сервер
   * ответил пятисоткой. На 4xx повтор ничего не изменит.
   *
   * Ограничение — срок, а не число попыток. Считать попытки бессмысленно:
   * перезапуск контейнера занимает под минуту, и шесть попыток с ростом паузы
   * выдыхались за семь секунд до того, как сервер поднимался. Проверено.
   */
  async durable(url, body, { deadlineMs = 180000, onRetry } = {}) {
    const key = body.key || crypto.randomUUID();
    const until = Date.now() + deadlineMs;
    for (let i = 1; ; i += 1) {
      try {
        return await json('POST')(url, { ...body, key });
      } catch (e) {
        const retryable = !e.status || e.status >= 500;
        if (!retryable || Date.now() >= until) throw e;
        if (onRetry) onRetry(i, e);
        await new Promise((r) => setTimeout(r, Math.min(1000 * 2 ** i, 8000)));
      }
    }
  },
};
